// Maps profile records to engine inputs and snapshot blocks, and the two
// gate predicates the server and the client both apply (spec §2).
const { region, REGION, vatNumberMatchesCountry } = require('./regions');
const { VAT_STATUS, CUSTOMER_TYPE, VALIDATION } = require('./resolve');

const addressOf = tax => (tax && tax.address) || {};

const partyFromTax = tax => ({
  country: addressOf(tax).country || '',
  postalCode: addressOf(tax).postcode || '',
});

// Has the provider declared a usable VAT status? (the gate predicate)
const vatDeclared = tax => {
  if (!tax || !tax.vatStatus) return false;
  const r = region(partyFromTax(tax));
  switch (tax.vatStatus) {
    case VAT_STATUS.REGISTERED:
      return r === REGION.NON_EU
        ? !!(tax.homeVat && typeof tax.homeVat.rate === 'number')
        : !!tax.vatNumber;
    case VAT_STATUS.EXEMPT_SMALL_BUSINESS:
      return r !== REGION.NON_EU;
    case VAT_STATUS.INVOICING_SERVICE:
      return !!(tax.invoicingService && tax.invoicingService.name);
    default:
      return false;
  }
};

const sellerFromTax = tax => {
  const t = tax || {};
  const { country, postalCode } = partyFromTax(t);
  const isInvoicing = t.vatStatus === VAT_STATUS.INVOICING_SERVICE;
  const vatNumber = isInvoicing
    ? (t.invoicingService && t.invoicingService.vatNumber) || null
    : t.vatNumber || t.vat || null;
  return {
    country,
    postalCode,
    vatStatus: t.vatStatus || null,
    vatNumber,
    homeVat: t.homeVat || null,
    exemptId: t.exemptId || null,
  };
};

const sellerName = t => {
  const name = t.name || {};
  return name.legalName || [name.firstName, name.lastName].filter(Boolean).join(' ') || null;
};

const sellerBusinessId = t =>
  (t.identifier && t.identifier.type === 'YTUNNUS' && t.identifier.plaintext) ||
  (t.businessRegistration && t.businessRegistration.number) ||
  null;

const sellerSnapshotFromTax = tax => {
  const t = tax || {};
  const s = sellerFromTax(t);
  return {
    name: sellerName(t),
    country: s.country,
    region: region(s),
    vatStatus: s.vatStatus,
    vatNumber: s.vatNumber,
    businessId: sellerBusinessId(t),
    invoicingService: t.invoicingService || null,
    exemptId: s.exemptId,
  };
};

const billingComplete = b => {
  if (!b || !b.country || !b.postalCode) return false;
  if (b.customerType === CUSTOMER_TYPE.CONSUMER) return true;
  if (b.customerType !== CUSTOMER_TYPE.BUSINESS) return false;
  if (!(b.legalName && b.street && b.city)) return false;
  // A number issued by another country than the billing country cannot be
  // priced (regions.js vatNumberMatchesCountry): the customer must fix one.
  return !b.vatNumber || vatNumberMatchesCountry(b.vatNumber, b.country);
};

// Invoice customers approved before the billing profile existed: derive one
// from their invoicing request so they are not blocked (spec §2).
const billingFromInvoicing = invoicing => {
  if (!invoicing) return null;
  const b = invoicing.billing || {};
  return {
    customerType: CUSTOMER_TYPE.BUSINESS,
    legalName: b.legalName || invoicing.companyName || null,
    country: b.country || invoicing.country || null,
    postalCode: b.postalCode || null,
    street: b.street || null,
    city: b.city || null,
    vatNumber: b.vatNumber || null,
    businessProofRef: b.registrationNumber || invoicing.businessId || null,
    updatedAt: null,
    source: 'invoicing',
  };
};

const buyerFromBilling = (billing, vatValidationStatus) => {
  const b = billing || {};
  return {
    country: b.country || '',
    postalCode: b.postalCode || '',
    customerType:
      b.customerType === CUSTOMER_TYPE.BUSINESS ? CUSTOMER_TYPE.BUSINESS : CUSTOMER_TYPE.CONSUMER,
    vatNumber: b.vatNumber || null,
    vatValidationStatus: vatValidationStatus || VALIDATION.NONE,
    businessProofRef: b.businessProofRef || null,
  };
};

const buyerSnapshotFromBilling = (billing, vatValidationStatus, fallbackName) => {
  const buyer = buyerFromBilling(billing, vatValidationStatus);
  return {
    name: (billing && billing.legalName) || fallbackName || null,
    country: buyer.country,
    region: region(buyer),
    customerType: buyer.customerType,
    vatNumber: buyer.vatNumber,
    vatValidationStatus: buyer.vatValidationStatus,
    businessProofRef: buyer.businessProofRef,
  };
};

// Soft reminders (spec §5): migrated statuses want a confirmation; exempt
// statuses want a yearly reconfirmation (Notion 8.1).
const vatStatusReminder = (tax, now = new Date()) => {
  if (!tax || !tax.vatStatus) return null;
  const history = Array.isArray(tax.vatStatusHistory) ? tax.vatStatusHistory : [];
  const latest = history[history.length - 1];
  if (!latest) return null;
  if (latest.declaredBy === 'migration') return 'confirmMigrated';
  const yearStart = `${now.getUTCFullYear()}-01-01`;
  if (
    tax.vatStatus === VAT_STATUS.EXEMPT_SMALL_BUSINESS &&
    String(latest.declaredAt || '') < yearStart
  ) {
    return 'reconfirmExempt';
  }
  return null;
};

module.exports = {
  vatDeclared,
  sellerFromTax,
  sellerSnapshotFromTax,
  billingComplete,
  billingFromInvoicing,
  buyerFromBilling,
  buyerSnapshotFromBilling,
  vatStatusReminder,
};
